import { filterObject } from "@/utils/helpers";
import { useAxios } from "@/utils/hooks/useAxios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSystem } from "@/utils/hooks/useLocation";

export const useGetNusukCardsBatchesQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const filteredParams = filterObject(params);
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["nusuk-cards-batches", filteredParams],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/nusuk-cards-batchs`, {
        params: {...filteredParams, system: currentSystem},
      }),
    select: (data) => data?.data,
  });
};

/** Paginated list of Nusuk card incident reports (batch-related). */
export const useGetNusukIncidentReportsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const filteredParams = filterObject(params);
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["nusuk-incident-reports", filteredParams],
    queryFn: () =>
      axiosProtected.get(
        `${currentSystem}/nusuk-cards-batchs/incident-reports`,
        { params: filteredParams }
      ),
    select: (res) => res?.data,
  });
};

/** Adjust URL/body if backend differs. */
export const useCompleteNusukIncidentReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ reportId, recipient_name, report_authority_id }) =>
      axiosProtected.post(
        `${currentSystem}/nusuk-cards-batchs/incident-reports/${reportId}/complete`,
        filterObject({ recipient_name, report_authority_id })
      ),
    onSuccess: () => {
      toast.success(t("CHANGES_SAVED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["nusuk-incident-reports"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("SOMETHING_WENT_WRONG"));
    },
  });
};

/** Form 351 PDF for selected incident report ids — adjust route if backend differs. */
export const usePrintNusukIncidentReportsForm351Mutation = () => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: (incidentIds) =>
      axiosProtected.post(
        `${currentSystem}/nusuk-cards-batchs/incident-reports/print-form-351`,
        { incident_ids: incidentIds },
        { responseType: "blob" }
      ),
  });
};

export const useGetOneNusukCardsBatchQuery = (id, options = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["one-nusuk-cards-batch", id],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/nusuk-cards-batchs/${id}`),
    enabled: Boolean(id) && (options.enabled ?? true),
  });
};

export const useNusukBatchLookupMutation = () => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ type, number }) =>
      axiosProtected.get(`${currentSystem}/nusuk-cards-batchs/lookup`, {
        params: { type, number },
      }),
  });
};

export const useNusukCardsLookupPilgrimsMutation = () => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ type, number, ...params }) =>
      axiosProtected.get(`${currentSystem}/nusuk-cards-batchs/lookup-pilgrims`, {
        params: filterObject({ type, number, ...params }),
      }),
  });
};

export const useCreateNusukCardsBatchMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: (payload) =>
      axiosProtected.post(`${currentSystem}/nusuk-cards-batchs`, payload),
    onSuccess: () => {
      toast.success(t("NUSUK_BATCH_CREATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["nusuk-cards-batches"] });
    },
  });
};

/** POST same resource URL with id — matches API “edit batch” contract. */
export const useUpdateNusukCardsBatchMutation = () => {
  const { axiosProtected } = useAxios();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ batchId, payload }) =>
      axiosProtected.post(
        `${currentSystem}/nusuk-cards-batchs/update/${batchId}`,
        payload
      ),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ["nusuk-cards-batches"] });
      queryClient.invalidateQueries({
        queryKey: ["one-nusuk-cards-batch", String(variables?.batchId)],
      });
    },
  });
};

/** PDF report — always Makkah report route. */
export const usePrintNusukCardsBatchMutation = () => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: (batchNos) =>
      axiosProtected.post(
        `${currentSystem}/reports/nusuk-cards-batch`,
        { batch_no: (Array.isArray(batchNos) ? batchNos : [batchNos]).map(String) },
        { responseType: "blob" }
      ),
  });
};

/** Lookup list for batch return reason dropdown (GET …/returns/reasons). */
export const useGetNusukCardsReturnReasonsQuery = (options = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["nusuk-cards-return-reasons", currentSystem],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/nusuk-cards-batchs/returns/reasons`),
    select: (res) => {
      const raw = res?.data?.data ?? res?.data;
      if (Array.isArray(raw)) return raw;
      if (Array.isArray(raw?.data)) return raw.data;
      return [];
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useReturnNusukCardsBatchMutation = () => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ batchId, payload }) =>
      axiosProtected.post(
        `${currentSystem}/nusuk-cards-batchs/${batchId}/returns`,
        payload
      ),
  });
};

export const useChangeNusukCardsBatchDeliveryStatusMutation = () => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ batchId, delivery_status }) =>
      axiosProtected.post(
        `${currentSystem}/nusuk-cards-batchs/${batchId}/change-delivery-status`,
        { delivery_status }
      ),
  });
};

/** Paginated list of Nusuk card batch returns (GET …/returns). */
export const useGetNusukCardsReturnsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const filteredParams = filterObject(params);
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["nusuk-cards-returns", currentSystem, filteredParams],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/nusuk-cards-batchs/returns`, {
        params: { ...filteredParams, system: currentSystem },
      }),
    select: (res) => res?.data,
  });
};

/** Single return record for view/edit (GET …/returns/:id). */
export const useGetOneNusukCardsReturnQuery = (returnId, options = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["one-nusuk-cards-return", currentSystem, returnId],
    queryFn: () =>
      axiosProtected.get(
        `${currentSystem}/nusuk-cards-batchs/returns/${returnId}`
      ),
    enabled: Boolean(returnId) && (options.enabled ?? true),
  });
};

export const useUpdateNusukCardsReturnMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ batchId, returnId, payload }) =>
      axiosProtected.post(
        `${currentSystem}/nusuk-cards-batchs/${batchId}/returns/update/${returnId}`,
        payload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["nusuk-cards-returns"] });
      queryClient.invalidateQueries({ queryKey: ["one-nusuk-cards-return"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("SOMETHING_WENT_WRONG"));
    },
  });
};

export const useDeleteNusukCardsReturnMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ batchId, returnId }) =>
      axiosProtected.delete(
        `${currentSystem}/nusuk-cards-batchs/${batchId}/returns/${returnId}`
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["nusuk-cards-returns"] });
      queryClient.invalidateQueries({ queryKey: ["one-nusuk-cards-return"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("SOMETHING_WENT_WRONG"));
    },
  });
};
